import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

type ClaimState =
  | 'loading'
  | 'needs_signin'
  | 'calling'
  | 'success'
  | 'org_not_found'
  | 'org_already_has_admin'
  | 'no_valid_invitation'
  | 'not_authenticated'
  | 'invitation_link_invalid'
  | 'unknown_error';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Module-scope in-flight guard: survives StrictMode double-invoke.
const inFlight = new Set<string>();

export default function Claim() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const orgId = params.get('org') ?? '';
  const orgIdValid = UUID_RE.test(orgId);

  const [state, setState] = useState<ClaimState>(orgIdValid ? 'loading' : 'invitation_link_invalid');
  const [attempt, setAttempt] = useState(0);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  // Drive state based on auth.
  useEffect(() => {
    if (!orgIdValid) return;
    if (authLoading) {
      setState('loading');
      return;
    }
    if (!user) {
      setState('needs_signin');
      return;
    }
    // user is present — kick off RPC
    setState('calling');
  }, [authLoading, user, orgIdValid]);

  // RPC effect — runs when state transitions to 'calling'.
  useEffect(() => {
    if (state !== 'calling') return;
    if (!orgIdValid) return;
    if (inFlight.has(orgId)) return;
    inFlight.add(orgId);

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('claim_first_admin', { p_org_id: orgId });
        if (cancelled) return;
        if (error) {
          console.error('[auth/claim] claim_first_admin error:', error);
          setErrorDetail(error.message || JSON.stringify(error));
          setState('unknown_error');
          return;
        }
        const result = data as any;
        console.log('[auth/claim] claim_first_admin result:', result);
        if (result?.ok && (result.promoted || result.already_admin)) {
          setState('success');
          return;
        }
        switch (result?.error) {
          case 'org_not_found':
            setState('org_not_found');
            break;
          case 'org_already_has_admin':
            setState('org_already_has_admin');
            break;
          case 'no_valid_invitation':
            setState('no_valid_invitation');
            break;
          case 'not_authenticated':
            setState('not_authenticated');
            break;
          default:
            setErrorDetail(result?.error ?? 'unknown');
            setState('unknown_error');
        }
      } catch (e) {
        console.error('[auth/claim] unexpected exception:', e);
        if (!cancelled) {
          setErrorDetail((e as Error)?.message ?? String(e));
          setState('unknown_error');
        }
      } finally {
        inFlight.delete(orgId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state, orgId, orgIdValid, attempt]);

  // Auto-redirect on success.
  useEffect(() => {
    if (state !== 'success') return;
    const t = setTimeout(() => navigate('/app/admin', { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [state, navigate]);

  const signInHref = `/auth/signin?next=${encodeURIComponent(`/auth/claim?org=${orgId}`)}`;

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-background p-4 gap-6">
      <Link
        to="/"
        aria-label="CultureLens"
        className="inline-block transition-opacity hover:opacity-80"
      >
        <span
          className="text-2xl font-display text-primary"
          style={{ letterSpacing: '0.5px' }}
        >
          CultureLens
        </span>
      </Link>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-display">Accept invitation</CardTitle>
          <CardDescription>
            {state === 'success'
              ? "You're all set."
              : state === 'needs_signin' || state === 'not_authenticated'
              ? 'Sign in to continue.'
              : state === 'calling' || state === 'loading'
              ? 'One moment…'
              : 'Invitation status'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {state === 'loading' && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking your invitation…
            </div>
          )}

          {state === 'calling' && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Setting you up…
            </div>
          )}

          {(state === 'needs_signin' || state === 'not_authenticated') && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Sign in to accept this invitation.
              </p>
              <Button asChild className="w-full">
                <Link to={signInHref}>Sign in</Link>
              </Button>
            </div>
          )}

          {state === 'success' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Taking you to your admin console…
              </p>
              <Button className="w-full" onClick={() => navigate('/app/admin', { replace: true })}>
                Go to admin console
              </Button>
            </div>
          )}

          {state === 'org_not_found' && (
            <p className="text-sm text-muted-foreground">
              This invitation link is invalid. Contact your administrator.
            </p>
          )}

          {state === 'invitation_link_invalid' && (
            <p className="text-sm text-muted-foreground">
              This invitation link is invalid. Contact your administrator.
            </p>
          )}

          {state === 'org_already_has_admin' && (
            <p className="text-sm text-muted-foreground">
              This organization already has an administrator. Contact your administrator if that looks wrong.
            </p>
          )}

          {state === 'no_valid_invitation' && (
            <p className="text-sm text-muted-foreground">
              Your invitation has expired or been revoked. Contact your platform administrator to
              request a new one.
            </p>
          )}

          {state === 'unknown_error' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Something went wrong. Please try again, or contact your platform administrator if
                this keeps happening.
              </p>
              {errorDetail && (
                <p className="text-xs text-destructive break-all">{errorDetail}</p>
              )}
              <Button
                className="w-full"
                onClick={() => {
                  setAttempt((n) => n + 1);
                  setState('calling');
                }}
              >
                Try again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
